// src/pages/ProfileOptionsPage.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteMyAccountData,
  signOutEverywhere,
} from "../services/accountDataService";
import "./ProfileOptionsPage.css";

import GateIcon from "../components/GateIcon";
import SpeechIcon from "../components/SpeechIcon";
import OfflineBanner from "../components/OfflineBanner";

import { useAuth } from "../auth/AuthProvider";

import {
  getBlockedUsersData,
  unblockUserData,
} from "../services/moderationDataService";

function ProfileOptionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const myUid = user?.uid || null;

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [userToUnblock, setUserToUnblock] = useState(null);
  const [actionToast, setActionToast] = useState({ visible: false, text: "" });

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState("");

  const showActionToast = (text) => {
    setActionToast({ visible: true, text });

    setTimeout(() => {
      setActionToast({ visible: false, text: "" });
    }, 2200);
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });

        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (ProfileOptionsPage):", err);
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  useEffect(() => {
    let mounted = true;

    const loadBlockedUsers = async () => {
      if (!myUid) {
        setBlockedUsers([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError("");

      try {
        const list = await getBlockedUsersData(myUid);

        if (!mounted) return;

        setBlockedUsers(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn("Blockierte Nutzer konnten nicht geladen werden:", error);

        if (!mounted) return;

        setBlockedUsers([]);
        setLoadError("Blockierte Nutzer konnten momentan nicht geladen werden.");
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadBlockedUsers();

    return () => {
      mounted = false;
    };
  }, [myUid, retryAttempt]);

  const clearLocalUserDataAfterDelete = () => {
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("userProfile_cache_v1");
      localStorage.removeItem("forum_seen_replies_v1");
      localStorage.removeItem("forum_badge_cache_v1");
      localStorage.removeItem("footerTexts_cache_v1");

      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("ee_") ||
          key.startsWith("edens_") ||
          key.includes("user") ||
          key.includes("profile")
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn("Lokale Nutzerdaten konnten nicht vollständig gelöscht werden:", e);
    }
  };

  const withDeleteFlowTimeout = (promise, ms = 6000) => {
    return Promise.race([
      promise,
      new Promise((resolve) => {
        setTimeout(() => resolve("timeout"), ms);
      }),
    ]);
  };

  const finishDeletedAccountFlow = async () => {
    clearLocalUserDataAfterDelete();

    try {
      await withDeleteFlowTimeout(signOutEverywhere(), 5000);
    } catch (e) {
      console.warn("Sign-out nach Account-Löschung nicht sauber abgeschlossen:", e);
    }

    setShowDeleteAccountModal(false);
    setDeleteAccountConfirmText("");
    setIsDeletingAccount(false);

    navigate("/login", { replace: true });

    setTimeout(() => {
      try {
        window.location.replace("/login");
      } catch (e) {
        console.warn("Fallback-Redirect fehlgeschlagen:", e);
      }
    }, 800);
  };

  const handleConfirmDeleteAccount = async () => {
    if (!myUid) return;

    if (deleteAccountConfirmText.trim().toLowerCase() !== "löschen") {
      showActionToast('Bitte gib "löschen" ein.');
      return;
    }

    setIsDeletingAccount(true);

    try {
      const result = await deleteMyAccountData(user);

      if (result?.ok === false) {
        throw new Error(result?.error || "delete-failed");
      }

      await finishDeletedAccountFlow();
    } catch (error) {
      console.warn("Profil konnte nicht gelöscht werden:", {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        full: error,
      });

      const message = String(error?.message || "").toLowerCase();

      if (
        message.includes("timeout") ||
        message.includes("deadline") ||
        message.includes("network")
      ) {
        await finishDeletedAccountFlow();
        return;
      }

      if (
        message.includes("requires-recent-login") ||
        message.includes("recent login") ||
        message.includes("auth/requires-recent-login")
      ) {
        setIsDeletingAccount(false);
        showActionToast("Bitte melde dich neu an und versuche es erneut.");
        return;
      }

      setIsDeletingAccount(false);
      showActionToast("Profil konnte nicht gelöscht werden.");
    }
  };

  const handleAskUnblock = (blockedUser) => {
    setUserToUnblock(blockedUser);
  };

  const handleConfirmUnblock = async () => {
    if (!myUid || !userToUnblock?.blockedUserId) {
      setUserToUnblock(null);
      return;
    }

    try {
      await unblockUserData(myUid, userToUnblock.blockedUserId);

      setBlockedUsers((prev) =>
        prev.filter(
          (entry) =>
            String(entry.blockedUserId) !== String(userToUnblock.blockedUserId)
        )
      );

      setUserToUnblock(null);
      showActionToast("Nutzer wurde entsperrt");
    } catch (error) {
      console.warn("Nutzer konnte nicht entsperren:", error);

      setUserToUnblock(null);
      showActionToast("Entsperren nicht möglich");
    }
  };

  const formatDate = (entry) => {
    const raw = entry?.createdAtClient || entry?.createdAt;

    if (!raw) return "";

    try {
      if (typeof raw?.toDate === "function") {
        return raw.toDate().toLocaleDateString("de-DE");
      }

      if (typeof raw?.toMillis === "function") {
        return new Date(raw.toMillis()).toLocaleDateString("de-DE");
      }

      const parsed = Date.parse(raw);
      if (Number.isFinite(parsed)) {
        return new Date(parsed).toLocaleDateString("de-DE");
      }

      return "";
    } catch {
      return "";
    }
  };

  return (
    <div className="ProfileOptionsPage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Edens Ernte Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />

      <SpeechIcon className="speech-icon no-tap-highlight" />
      <GateIcon className="login-icon" />

      <img
        src="/rezept-icon.png"
        alt="Rezepthelfer Icon"
        className="rezept-icon"
        onClick={() => navigate("/rezepte")}
      />

      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      <div className="profile-options-content">
        {actionToast.visible && (
          <div className="mainbox-toast">{actionToast.text}</div>
        )}

        <div className="profile-options-header-row">
          <h2>Profil-Optionen</h2>

          <button
            type="button"
            className="profile-options-back-btn"
            onClick={() => navigate("/profil")}
          >
            Zurück
          </button>
        </div>

        <div className="profile-options-section">
          <h3>Blockierte Nutzer</h3>

          <p className="profile-options-info">
            Hier kannst du Nutzer entsperren, die du zuvor blockiert hast.
          </p>

          {isLoading && (
            <p className="profile-options-muted">
              Blockierte Nutzer werden geladen...
            </p>
          )}

          {loadError && (
            <div className="error-message profile-options-error">
              {loadError}
            </div>
          )}

          {!isLoading && !loadError && blockedUsers.length === 0 && (
            <p className="profile-options-muted">
              Du hast aktuell keine Nutzer blockiert.
            </p>
          )}

          {!isLoading && blockedUsers.length > 0 && (
            <ul className="blocked-user-list">
              {blockedUsers.map((entry) => {
                const displayName =
                  entry.blockedUserName ||
                  entry.blockedUserId ||
                  "Unbekannter Nutzer";

                const blockedDate = formatDate(entry);

                return (
                  <li key={entry.blockedUserId || entry.id}>
                    <div className="blocked-user-text">
                      <strong>{displayName}</strong>
                      {blockedDate && <span>Blockiert seit {blockedDate}</span>}
                    </div>

                    <button
                      type="button"
                      className="unblock-btn"
                      onClick={() => handleAskUnblock(entry)}
                    >
                      Entsperren
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="profile-options-section profile-options-danger-zone">
          <h3>Profil löschen</h3>

          <p className="profile-options-info">
            Hier kannst du dein Edens-Ernte-Profil dauerhaft löschen. Dabei
            werden dein Benutzerkonto und zugehörige Profildaten entfernt.
          </p>

          <button
            type="button"
            className="delete-account-btn"
            onClick={() => setShowDeleteAccountModal(true)}
            disabled={!myUid || isDeletingAccount}
          >
            Profil dauerhaft löschen
          </button>
        </div>
      </div>

      {userToUnblock && (
        <div className="modal-overlay" onClick={() => setUserToUnblock(null)}>
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Nutzer entsperren?</h3>

            <p>
              Möchtest du{" "}
              <strong>
                {userToUnblock.blockedUserName ||
                  userToUnblock.blockedUserId ||
                  "diesen Nutzer"}
              </strong>{" "}
              wirklich entsperren?
            </p>

            <div className="modal-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setUserToUnblock(null)}
              >
                Abbrechen
              </button>

              <button
                type="button"
                className="green-button"
                onClick={handleConfirmUnblock}
              >
                Entsperren
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteAccountModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            if (!isDeletingAccount) {
              setShowDeleteAccountModal(false);
              setDeleteAccountConfirmText("");
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Profil dauerhaft löschen?</h3>

            <p>
              Dein Benutzerkonto wird dauerhaft gelöscht. Diese Aktion kann
              nicht rückgängig gemacht werden.
            </p>

            <p>
              Zur Bestätigung gib bitte <strong>löschen</strong> ein.
            </p>

            <input
              type="text"
              className="delete-account-input"
              value={deleteAccountConfirmText}
              onChange={(event) => setDeleteAccountConfirmText(event.target.value)}
              placeholder="löschen"
              disabled={isDeletingAccount}
            />

            <div className="modal-buttons">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteAccountModal(false);
                  setDeleteAccountConfirmText("");
                }}
                disabled={isDeletingAccount}
              >
                Abbrechen
              </button>

              <button
                type="button"
                className="delete-btn"
                onClick={handleConfirmDeleteAccount}
                disabled={
                  isDeletingAccount ||
                  deleteAccountConfirmText.trim().toLowerCase() !== "löschen"
                }
              >
                {isDeletingAccount ? "Wird gelöscht…" : "Endgültig löschen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileOptionsPage;